// src/components/attendance/centralAvailabilityLinks.ts
//
// Where the attendance overflow menu sends people.
//
// Central owns the schedule and leave editors. CRM deliberately has none of
// its own — building a second one is exactly how two sources of truth start —
// so these are LINKS to the canonical Central surface, not local routes.
//
// `VITE_CENTRAL_URL` is the same variable CRM already uses to reach Central for
// profile and settings deep-links; nothing new is introduced here.

const CENTRAL_URL = ((import.meta.env.VITE_CENTRAL_URL as string | undefined) ?? '').replace(/\/+$/, '');

/**
 * The member self-service surface, relative to a workspace.
 *
 * Central scopes this surface per workspace: `/workspace/<id>/settings/…` is
 * what renders the schedule, leave and clock state. The unscoped
 * `/settings/my-availability` resolves to a page that renders none of it —
 * browser certification landed there and found an empty settings shell — so a
 * link without the workspace segment reaches Central without reaching
 * canonical management.
 */
export const MY_WORK_AVAILABILITY_PATH = '/settings/my-availability';
export const myWorkAvailabilityPathFor = (workspaceId: string): string =>
  `/workspace/${encodeURIComponent(workspaceId)}${MY_WORK_AVAILABILITY_PATH}`;

/**
 * Absolute URL of the canonical surface, or `null` when Central's web origin is
 * not configured for this deployment.
 *
 * Returning null is deliberate: the overflow renders no destination at all
 * rather than a link to `/settings/my-availability` on the CRM origin, which
 * would 404 and look like the feature is broken rather than unconfigured.
 *
 * `workspaceId` is optional only so an unknown active workspace degrades to the
 * previous behaviour instead of losing the menu entirely; supply it whenever it
 * is known, which on every surface that renders this menu it is.
 */
export function myWorkAvailabilityUrl(workspaceId?: string | null): string | null {
  if (!CENTRAL_URL) return null;
  return workspaceId
    ? `${CENTRAL_URL}${myWorkAvailabilityPathFor(workspaceId)}`
    : `${CENTRAL_URL}${MY_WORK_AVAILABILITY_PATH}`;
}

/** Open the canonical surface. Separate from the URL builder so it is testable. */
export function openMyWorkAvailability(
  workspaceId?: string | null,
  open: (url: string) => void = (u) => window.open(u, '_blank', 'noopener'),
): boolean {
  const url = myWorkAvailabilityUrl(workspaceId);
  if (!url) return false;
  open(url);
  return true;
}
